import { useEffect, useState } from 'react';

/**
 * One 1s interval for the whole queue list -- individual SlaCountdown displays
 * are derived from this single elapsed value rather than each running their
 * own timer. `dataUpdatedAt` (from useQuery) anchors the elapsed calculation;
 * every refetch moves that anchor forward, so any local drift between ticks
 * self-corrects on the next fetch instead of accumulating.
 *
 * Optimistic cache writes move the anchor too: the realtime lead-assigned
 * prepend (useCallCenterAgentChannel) bumps dataUpdatedAt without refreshing
 * any seconds_remaining, so for a beat the OTHER rows' countdowns re-inflate
 * slightly (elapsed resets to 0 against values from the older fetch) until
 * the follow-up invalidation's refetch lands. Intentional anchor semantics --
 * a single shared anchor is what lets every drift self-correct -- so do not
 * "fix" it by decoupling rows from the anchor or excluding cache writes.
 *
 * A plain mount/unmount effect is intentional here (not useVisibilityInterval):
 * there's no need for the immediate-fire-on-mount or pause-on-hidden semantics
 * that hook provides, since elapsed time is derived from the wall clock and
 * stays correct even while the tab is hidden and the interval is throttled.
 */
export const useQueueCountdowns = (dataUpdatedAt: number): number => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  return Math.max(0, Math.floor((now - dataUpdatedAt) / 1000));
};
